import axios from 'axios'

import Auth from '../auth'
import { API_BASE, apiErrorHandler } from '../api'
import CSRF from '../csrf'
import { objectToSnakeCase, objectToCamelCase } from '../utils'

import Admin from '@/services/admins'
import Employee from '@/services/employees'

// API Endpoints
const VALIDATE_TOKEN_ENDPOINT = `${API_BASE}users/validate-token/`
const GET_SELF_ENDPOINT = `${API_BASE}users/get-self/`
const TWO_FACTOR_CHALLENGE = `${API_BASE}2fa/challenge/`
const TWO_FACTOR_VERIFY = `${API_BASE}2fa/verify/`
const PASSWORD_RESET_REQUEST_ENDPOINT = `${API_BASE}password/reset/`
const PASSWORD_RESET_CONFIRM_ENDPOINT = `${API_BASE}password/reset/confirm/`
const GENERATE_PATCH_PAYPAL_EMAIL_ENDPOINT = id => `${API_BASE}employees/${id}/paypal-email/`
const TWILIO_CHALLENGE = `${API_BASE}twilio/challenge/`
const TWILIO_VERIFY = `${API_BASE}twilio/verify/`

export default class UserAPI {
  /**
   * Instantiate a new `UserAPI`
   *
   * @param {class} cls - The class to use to create objects.
   */
  constructor(cls) {
    this.cls = cls
  }

  /**
   * Factory method to create a new instance of `UserAPI`.
   *
   * @param {class} cls - The class to use to create objects.
   **/
  static create(cls) {
    return new UserAPI(cls)
  }

  /**
   * Get the current user's own profile data via the API
   *
   * @important - Don't use this too often, b/c the response contains the user's
   *              authentication token.
   *
   * @param {string} token - User's access token from social auth.
   * @param {string} tokenType - User's access tokenType from social auth.
   *
   * @returns {Promise} - Contains the `User` data and auth token.
   */
  async getSelf({ token, tokenType = 'Bearer' }) {
    const url = GET_SELF_ENDPOINT
    const options = {
      headers: {
        Authorization: `${tokenType} ${token}`,
        ...Auth.getAcceptLanguageHeader(),
      },
    }

    let response
    try {
      response = await axios.get(url, options)
    } catch (err) {
      apiErrorHandler({ apiName: 'UserAPI.getSelf', enable400Alert: false })
      return
    }
    // Cast user to proper type and store in state
    let user = this.cls.fromAPI(response.data)
    if (user.isAdmin) {
      user = Admin.fromAPI(response.data)
    } else if (user.isEmployee) {
      user = Employee.fromAPI(response.data)
    }
    return user
  }

  /**
   * Check a user's authentication token against the API
   *
   * @returns {Promise} - Promise that resolves to true or false.
   */
  async validateToken(token) {
    const options = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Token ${token}`,
        ...Auth.getAcceptLanguageHeader(),
      },
    }
    const promise = axios.post(VALIDATE_TOKEN_ENDPOINT, {}, options)
    return promise
      .then(response => objectToCamelCase(response.data))
      .catch(error => {
        // eslint-disable-next-line no-console
        console.log('UserAPI.validateToken Error')
        throw error
      })
  }

  /**
   * Generate a two-factor challenge for this user with the specified device type.
   *
   * @param {Object} payload - Payload containing the `deviceType` to generate a challenge with.
   * @param {string} token - Authorization token to send with the request.
   */
  async twoFactorChallenge(payload, token) {
    const url = TWO_FACTOR_CHALLENGE
    const options = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Token ${token}`,
        ...CSRF.getHeaders(),
        ...Auth.getAcceptLanguageHeader(),
      },
    }
    const data = objectToSnakeCase(payload)

    try {
      const response = await axios.post(url, data, options)
      return response
    } catch (err) {
      apiErrorHandler({ apiName: 'UserAPI.twoFactorChallenge', enable400Alert: false })
      throw err
    }
  }

  /**
   * Verify a user with a two-factor authentication code.
   *
   * @param {Object} payload - Payload containing the `code` and the `deviceType` that generated the code.
   * @param {string} token - Authorization token to send with the request.
   */
  async twoFactorVerify(payload, token) {
    const url = TWO_FACTOR_VERIFY
    const options = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Token ${token}`,
        ...CSRF.getHeaders(),
        ...Auth.getAcceptLanguageHeader(),
      },
    }
    const data = objectToSnakeCase(payload)

    try {
      const response = await axios.post(url, data, options)
      let user = this.cls.fromAPI(response.data)
      if (user.isAdmin) {
        user = Admin.fromAPI(response.data)
      } else if (user.isEmployee) {
        user = Employee.fromAPI(response.data)
      }
      return user
    } catch (err) {
      apiErrorHandler({ apiName: 'UserAPI.twoFactorVerify', enable400Alert: false })
      throw err
    }
  }

  /**
   * Generate a twilio verification challenge for this employee with the specified device type.
   *
   * @param {Object} payload - Payload containing the `employee` to generate a challenge with.
   */
  async twilioChallenge(payload) {
    const url = TWILIO_CHALLENGE
    const options = {
      headers: {
        'Content-Type': 'application/json',
        ...Auth.getHeaders(),
      },
    }
    const data = objectToSnakeCase(payload)

    try {
      const response = await axios.post(url, data, options)
      return response
    } catch (err) {
      apiErrorHandler({ apiName: 'UserAPI.twilioChallenge', enable400Alert: false })
      throw err
    }
  }

  /**
   * Verify twilio code for this user with the specified device type.
   *
   * @param {Object} payload - Payload containing the `code` and 'employee' to verify the challenge with.
   */
  async twilioVerify(payload) {
    const url = TWILIO_VERIFY
    const options = {
      headers: {
        'Content-Type': 'application/json',
        ...Auth.getHeaders(),
      },
    }
    const data = objectToSnakeCase(payload)

    try {
      const response = await axios.post(url, data, options)
      return response
    } catch (err) {
      apiErrorHandler({ apiName: 'UserAPI.twilioVerify', enable400Alert: false })
      throw err
    }
  }

  /**
   * @function    requestPasswordReset
   *
   * Send a POST requesting to backend, attempting to trigger a password-reset email
   *
   * @param   {string}  email  - email inputted by user
   **/
  async requestPasswordReset(email) {
    const data = { email }
    const options = {
      headers: {
        ...Auth.getHeaders(),
      },
    }
    return axios
      .post(PASSWORD_RESET_REQUEST_ENDPOINT, data, options)
      .then(response => response.data)
      .catch(apiErrorHandler({ apiName: 'UserAPI.requestPasswordReset' }))
  }

  /**
   * @function    passwordResetConfirm
   * Send a POST requesting to backend, attempting to update a user's password with this payload
   *
   * @param   {string}  newPassword1  - New password input from user
   * @param   {string}  newPassword2  - Confirm new password input from user
   * @param   {string}  uid           - UUID retrieved from $route.params
   * @param   {string}  token         - token retrieved from $route.params
   **/
  async passwordResetConfirm(newPassword1, newPassword2, uid, token) {
    const data = {
      new_password1: newPassword1,
      new_password2: newPassword2,
      uid,
      token,
    }
    const options = {
      headers: {
        'Content-Type': 'application/json',
        ...Auth.getHeaders(),
      },
    }
    return axios
      .post(PASSWORD_RESET_CONFIRM_ENDPOINT, data, options)
      .then(response => response.data)
      .catch(apiErrorHandler({ enable400Alert: false, apiName: 'UserAPI.passwordResetConfirm' }))
  }
}
